import { estimateSurfFaceHeight, surfFaceFactor, type BreakType } from "./marine";
import type { BreakCalibration } from "./regions";

export type TideTrendForCalibration = "rising" | "falling" | "slack" | "steady";

export interface CalibratedSurfFaceInput {
  offshoreHeightM: number;
  periodS: number | null | undefined;
  breakType?: BreakType | null;
  swellDirectionDeg?: number | null;
  shoreNormal?: number | null;
  tideHeightM?: number | null;
  tideTrend?: TideTrendForCalibration | null;
  calibration?: BreakCalibration | null;
}

function finiteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

function angleDiff(a: number, b: number): number {
  let diff = Math.abs(a - b) % 360;
  if (diff > 180) diff = 360 - diff;
  return diff;
}

export function periodAmplification(
  periodS: number | null | undefined,
  breakType: BreakType = "beach",
  periodGain: number = 1,
): number {
  const period = finiteNumber(periodS) && periodS > 0 ? periodS : 8;
  const baseFactor = surfFaceFactor(period, breakType);
  return Math.max(0, 1 + (baseFactor - 1) * periodGain);
}

export function directionExposure(
  swellDirectionDeg: number | null | undefined,
  shoreNormal: number | null | undefined,
  calibration?: BreakCalibration | null,
): number {
  const hasDirectionCalibration =
    calibration?.directionWindowDeg != null || calibration?.backsideLeakage != null;
  if (
    !hasDirectionCalibration ||
    !finiteNumber(swellDirectionDeg) ||
    !finiteNumber(shoreNormal)
  ) {
    return 1;
  }

  const windowDeg = clamp(calibration?.directionWindowDeg ?? 90, 0, 180);
  const leakage = clamp(calibration?.backsideLeakage ?? 0.15, 0, 1);
  const diff = angleDiff(swellDirectionDeg, shoreNormal);
  if (diff <= windowDeg) return 1;

  const fadeRange = Math.max(1, 180 - windowDeg);
  const fade = clamp((diff - windowDeg) / fadeRange, 0, 1);
  return round2(1 - fade * (1 - leakage));
}

export function tideFactor(
  tideHeightM: number | null | undefined,
  _tideTrend: TideTrendForCalibration | null | undefined,
  calibration?: BreakCalibration | null,
): number {
  const sensitivity = clamp(calibration?.tideSensitivity ?? 0, 0, 1);
  if (
    sensitivity === 0 ||
    !finiteNumber(tideHeightM) ||
    !finiteNumber(calibration?.bestTideMinM) ||
    !finiteNumber(calibration?.bestTideMaxM)
  ) {
    return 1;
  }

  const minM = Math.min(calibration.bestTideMinM, calibration.bestTideMaxM);
  const maxM = Math.max(calibration.bestTideMinM, calibration.bestTideMaxM);
  const insideWindow = tideHeightM >= minM && tideHeightM <= maxM;
  const distanceM = insideWindow
    ? 0
    : Math.min(Math.abs(tideHeightM - minM), Math.abs(tideHeightM - maxM));
  const penalty = Math.min(0.35, distanceM * 0.26 * sensitivity);
  return round2(1 - penalty);
}

export function calibratedSurfFaceHeight({
  offshoreHeightM,
  periodS,
  breakType = "beach",
  swellDirectionDeg,
  shoreNormal,
  tideHeightM,
  tideTrend,
  calibration,
}: CalibratedSurfFaceInput): number {
  if (!calibration) {
    return estimateSurfFaceHeight(offshoreHeightM, periodS ?? 8, breakType ?? "beach");
  }

  const resolvedBreakType = breakType ?? "beach";
  const heightScale = calibration.heightScale ?? 1;
  return round2(
    offshoreHeightM *
      heightScale *
      periodAmplification(periodS, resolvedBreakType, calibration.periodGain ?? 1) *
      directionExposure(swellDirectionDeg, shoreNormal, calibration) *
      tideFactor(tideHeightM, tideTrend, calibration),
  );
}
